import { Divider, Stack } from "@mui/material"
import { Dispatch, Fragment, SetStateAction } from "react"

import { uuid } from "uuidv4"

import { ModifiedIncident } from ".."
import { Accordion } from "src/components/common/accordion"

import { Checkbox } from "src/components/common/checkbox"
import { create } from "src/helpers/bem"
import { useTranslation } from "src/hooks/translation"
import { Dialogs, useDialog } from "src/states/dialogs"
import {
  xRMWebshopOrderedCategories,
  xRMWebshopOrderedItem,
} from "src/types/xRM"

import styles from "./ProductCategory.module.scss"
import ProductRow from "./ProductRow"

const bem = create(styles, "ProductCategory")

type ProductCategoryProps = {
  selectedBookingId: string
  incident: ModifiedIncident
  category: xRMWebshopOrderedCategories
  setIncident: Dispatch<SetStateAction<ModifiedIncident>>
}

export const ProductCategory = ({
  category,
  incident,
  setIncident,
  selectedBookingId,
}: ProductCategoryProps) => {
  const { messages } = useTranslation()
  const { disabled: isReadOnly } = useDialog(
    "incidentDialog",
  ) as Dialogs["incidentDialog"]

  const { incidentPositions } = incident
  const { categoryId, categoryName, items } = category

  const productMessages =
    messages.pages.account.overview.profile.incidents.createDialog
      .cancellationFields.products
  const groupedItems = items?.reduce<Record<string, xRMWebshopOrderedItem[]>>(
    (acc, item) => {
      const key = item.senderName ?? "Unknown"
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    },
    {},
  )
  const itemsBySender = groupedItems ? Object.entries(groupedItems) : []
  const allItemsSelected =
    items?.every((item) =>
      incidentPositions?.some((ip) => ip.salesorderDetailId === item.id),
    ) ?? false
  const selectedItems = incidentPositions?.filter((ip) =>
    items?.some((item) => item.id === ip.salesorderDetailId),
  )

  const handleCheckboxChange = () => {
    if (allItemsSelected) {
      const idsToRemove = new Set(items?.map((obj) => obj.id))
      setIncident((prev) => ({
        ...prev,
        incidentPositions: prev.incidentPositions?.filter(
          (ip) => !idsToRemove.has(ip.salesorderDetailId ?? ""),
        ),
      }))
    } else {
      const allItemIds = items ? items.map((item) => item.id ?? "") : []
      setIncident((prev) => ({
        ...prev,
        incidentPositions: [
          ...(prev.incidentPositions ?? []),
          ...allItemIds.map((id) => ({
            id: uuid(),
            salesorderId: selectedBookingId,
            salesorderDetailId: id,
          })),
        ],
      }))
    }
  }

  return (
    <Accordion
      disableGutters
      colorVariant={"base"}
      detailsClassName={bem("accordion__details")}
      expanded={true}
      id={categoryId ?? ""}
      sx={{ padding: 0 }}
      classNames={{
        root: bem("accordion__root"),
        wrapper: bem("accordion__wrapper"),
      }}
      label={
        <div className={bem("accordion__label")}>
          <Checkbox
            checked={allItemsSelected}
            disabled={isReadOnly}
            onChange={handleCheckboxChange}
          />
          <div className={bem("accordion__label__text")}>{`${categoryName} ${
            selectedItems ? `(${selectedItems?.length})` : "(0)"
          }`}</div>
        </div>
      }
    >
      <Stack>
        {itemsBySender.map(([groupName, groupItems]) => (
          <Fragment key={groupName}>
            <Divider
              className={bem("accordion__item__divider")}
              variant="fullWidth"
            >
              {`${productMessages.senderName} ${groupName}`}
            </Divider>
            {groupItems.map((item) => (
              <ProductRow
                key={item.id}
                incident={incident}
                item={item}
                selectedBookingId={selectedBookingId}
                setIncident={setIncident}
              />
            ))}
          </Fragment>
        ))}
      </Stack>
    </Accordion>
  )
}

export default ProductCategory
